// @figma file=tbd node=tbd name="ActiveSessionsTable" set="araf-analytics" verified=tbd
import * as React from 'react';
import { Badge } from '../components/atoms/badge';
import { EmptyState } from '../components/atoms/empty-state';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/atoms/table';
import { cn } from '../lib/utils';
import type { ActiveSessionDto } from './types';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ActiveSessionsTableProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Session rows to display */
  sessions: ActiveSessionDto[];
  /**
   * Scope the table to a single tenant.
   * Pass undefined for cross-tenant admin view (shows tenantSlug column).
   */
  tenantSlug?: string;
  /** Render a loading skeleton instead of real rows */
  loading?: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatRelative(isoString: string): string {
  const diff = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(isoString).toLocaleDateString();
}

// ─── Component ────────────────────────────────────────────────────────────────

const ActiveSessionsTable = React.forwardRef<HTMLDivElement, ActiveSessionsTableProps>(
  ({ className, sessions, tenantSlug, loading = false, ...props }, ref) => {
    const showTenantColumn = tenantSlug === undefined;

    return (
      <div
        ref={ref}
        className={cn(
          'w-full overflow-auto rounded-[var(--radius)] border border-border',
          className,
        )}
        {...props}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              {showTenantColumn && <TableHead>Tenant</TableHead>}
              <TableHead>Last activity</TableHead>
              <TableHead>Session start</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <div className="h-4 w-40 rounded bg-muted animate-pulse" />
                  </TableCell>
                  {showTenantColumn && (
                    <TableCell>
                      <div className="h-4 w-24 rounded bg-muted animate-pulse" />
                    </TableCell>
                  )}
                  <TableCell>
                    <div className="h-4 w-20 rounded bg-muted animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-20 rounded bg-muted animate-pulse" />
                  </TableCell>
                </TableRow>
              ))
            ) : sessions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={showTenantColumn ? 4 : 3} className="py-10">
                  <EmptyState
                    heading="No active sessions"
                    description="There are no active sessions right now."
                  />
                </TableCell>
              </TableRow>
            ) : (
              sessions.map((s) => (
                <TableRow key={s.sessionId}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-foreground">
                        {s.email ?? s.userId}
                      </span>
                      {s.email && (
                        <span className="text-xs text-muted-foreground font-mono">{s.userId}</span>
                      )}
                    </div>
                  </TableCell>
                  {showTenantColumn && (
                    <TableCell>
                      <Badge variant="secondary" appearance="soft">
                        {s.tenantSlug}
                      </Badge>
                    </TableCell>
                  )}
                  <TableCell className="text-sm text-muted-foreground">
                    {formatRelative(s.lastActivityAt)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatRelative(s.loginAt)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    );
  },
);
ActiveSessionsTable.displayName = 'ActiveSessionsTable';

export { ActiveSessionsTable };
